import { accentuateApiRef } from '@internal/backstage-plugin-accentuate';
import { AccentuateResponse } from '@internal/backstage-plugin-accentuate-common';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi, alertApiRef } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { JsonObject } from '@backstage/types';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Button,
  Box,
  Chip,
} from '@material-ui/core';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import RepositoryIcon from '@material-ui/icons/Storage';

import { PropertyType } from '../../types';
import {
  SlackCommunicationValue,
  PropertyValueMap,
  EntityProperty,
} from '../../types';
import { MultivalueInput } from '../EntityActionMenu/GroupEntityActionMenu/MultivalueAnnotationInput';
import { SelectionOption } from '../EntityActionMenu/GroupEntityActionMenu/types';

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(3),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(3),
    border: `1px solid ${theme.palette.info.main}`,
  },
  infoIcon: {
    color: theme.palette.info.main,
    marginTop: theme.spacing(0.25),
  },
  infoText: {
    fontSize: '0.9rem',
    color: theme.palette.info.dark,
    lineHeight: 1.5,
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
  },
  formSection: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    border: `2px solid ${theme.palette.primary.main}`,
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  inputForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    width: '100%',
  },
  inputRow: {
    display: 'flex',
    gap: theme.spacing(2),
    alignItems: 'flex-start',
  },
  addButton: {
    minHeight: '40px',
    textTransform: 'none',
    fontWeight: 600,
    maxWidth: 200,
    alignSelf: 'flex-end',
  },

  itemsSection: {
    padding: theme.spacing(2),
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.paper
        : theme.palette.grey[50],
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
  },
  itemsSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1.5),
    fontSize: '0.95rem',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  itemCard: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.background.default
        : theme.palette.common.white,
    border: `1px solid ${
      theme.palette.type === 'dark'
        ? theme.palette.background.paper
        : theme.palette.grey[300]
    }`,
    borderRadius: theme.spacing(0.5),
    justifyContent: 'space-between',
  },
  itemInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    flex: 1,
  },
  itemName: {
    fontWeight: 500,
    fontSize: '0.95rem',
  },
  itemLink: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  removeButton: {
    minWidth: 'auto',
    padding: theme.spacing(0.5),
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
    fontSize: '0.9rem',
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  changesSummary: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  changesChip: {
    fontWeight: 600,
  },
  buttonGroup: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  saveButton: {
    minWidth: '120px',
  },
}));

interface EntityUpdateDialogProps {
  dialogTitle: string;
  property: EntityProperty;
  propertyOptions?: SelectionOption[];
  open: boolean;
  onClose?: () => void;
  loading?: boolean;
}

const getInitialFromProperty = (
  entity: Entity,
  property: EntityProperty,
): PropertyValueMap[PropertyType] => {
  switch (property.propertyType) {
    case 'COMMUNICATIONS_SLACK': {
      const communications =
        (entity.spec?.communications as Array<{
          type: string;
          channel: string;
          title: string;
        }>) ?? [];
      return communications
        .filter(comm => comm.type === 'slack')
        .map(comm => ({
          channel: comm.channel,
          title: comm.title,
        }));
    }
    case 'ANNOTATION': {
      const annotationValue =
        entity.metadata.annotations?.[property.propertyValue] ?? '';
      if (property.propertyValue === 'jira/project-key' && annotationValue) {
        return annotationValue
          .split(',')
          .map(key => key.trim())
          .filter(Boolean);
      }
      if (
        property.propertyValue === 'confluence/wiki-urls' &&
        annotationValue
      ) {
        return annotationValue
          .split(',')
          .map(url => url.trim())
          .filter(Boolean);
      }
      return annotationValue;
    }
    case 'OWNER':
      return (entity.spec?.owner as string) ?? '';
    case 'APIS':
      return (entity.spec?.providesApis as string[]) ?? [];
    case 'REPOSITORIES':
      return (entity.spec?.hasRepositories as string[]) ?? [];
    default:
      return '';
  }
};

const getDiffForType = (
  property: EntityProperty,
  value: PropertyValueMap[PropertyType],
  current?: AccentuateResponse,
): JsonObject => {
  const data = current?.data;
  const metadata = (data?.metadata as JsonObject) ?? {};
  const spec = (data?.spec as JsonObject) ?? {};

  if (property.propertyType === 'ANNOTATION') {
    const annotations =
      (metadata.annotations as Record<string, JsonObject[keyof JsonObject]>) ??
      {};

    let annotationValue: JsonObject[keyof JsonObject] =
      value as JsonObject[keyof JsonObject];

    if (Array.isArray(value)) {
      const stringified = value.map(v => {
        if (v === null || v === undefined) {
          return '';
        }
        if (typeof v === 'object') {
          return JSON.stringify(v);
        }
        return String(v);
      });

      if (
        property.propertyValue === 'jira/project-key' ||
        property.propertyValue === 'confluence/wiki-urls'
      ) {
        annotationValue = stringified.join(', ');
      }
    }

    return {
      metadata: {
        ...metadata,
        annotations: {
          ...annotations,
          [property.propertyValue]: annotationValue,
        },
      },
      spec: { ...spec },
    };
  }

  if (property.propertyType === 'COMMUNICATIONS_SLACK') {
    const communications = (spec.communications as JsonObject[]) ?? [];
    const slackChannels = value as SlackCommunicationValue[];

    const nonSlackCommunications = communications.filter(
      (comm: JsonObject) => comm.type !== 'slack',
    );

    const slackCommunications = slackChannels.map(ch => ({
      type: 'slack',
      channel: ch.channel,
      title: ch.title,
    }));

    return {
      metadata: { ...metadata },
      spec: {
        ...spec,
        communications: [...nonSlackCommunications, ...slackCommunications],
      },
    };
  }

  const updates: JsonObject = {};
  if (property.propertyType === 'OWNER') {
    updates.owner = value as string;
  } else if (property.propertyType === 'APIS') {
    updates.providesApis = value as string[];
  } else if (property.propertyType === 'REPOSITORIES') {
    updates.hasRepositories = value as string[];
  }

  return {
    metadata: { ...metadata },
    spec: { ...spec, ...updates },
  };
};

const normalizeToArray = (value: PropertyValueMap[PropertyType]): string[] => {
  if (Array.isArray(value)) {
    return value as string[];
  }
  if (value) {
    return [value as string];
  }
  return [];
};

export const EntityUpdateDialog = ({
  dialogTitle,
  open,
  property,
  propertyOptions,
  onClose,
  loading,
}: EntityUpdateDialogProps) => {
  const classes = useStyles();
  const { entity } = useEntity();
  const accentuateApi = useApi(accentuateApiRef);
  const alertApi = useApi(alertApiRef);

  const initialValue = getInitialFromProperty(entity, property);
  const [changedValue, setChangedValue] =
    useState<PropertyValueMap[PropertyType]>(initialValue);
  const [newSlackChannel, setNewSlackChannel] = useState({
    channel: '',
    title: '',
  });
  const [channelError, setChannelError] = useState<string | null>(null);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [newProjectKey, setNewProjectKey] = useState('');
  const [projectError, setProjectError] = useState<string | null>(null);
  const [newConfluenceUrl, setNewConfluenceUrl] = useState('');
  const [confluenceUrlError, setConfluenceUrlError] = useState<string | null>(
    null,
  );

  React.useEffect(() => {
    if (open) {
      const currentValue = getInitialFromProperty(entity, property);
      setChangedValue(currentValue);
      setNewSlackChannel({ channel: '', title: '' });
      setChannelError(null);
      setTitleError(null);
      setNewProjectKey('');
      setProjectError(null);
      setNewConfluenceUrl('');
      setConfluenceUrlError(null);
    }
  }, [open, entity, property]);

  const validateSlackChannel = (channel: string) => {
    if (!channel) return 'Slack channel is required';
    if (!channel.startsWith('#')) return 'Channel must start with #';
    return null;
  };

  const validateSlackTitle = (title: string) => {
    if (!title) return 'Slack URL is required';
    try {
      const url = new URL(title);
      if (
        url.hostname !== 'tomtomslack.slack.com' ||
        url.protocol !== 'https:'
      ) {
        return 'Must be a valid Slack URL';
      }
      return null;
    } catch {
      return 'Must be a valid Slack URL';
    }
  };

  const isValidUrl = (value: string): boolean => {
    try {
      const url = new URL(value);
      return url.href.length > 0;
    } catch {
      return false;
    }
  };

  const handleAddSlackChannel = () => {
    const channelErr = validateSlackChannel(newSlackChannel.channel);
    const titleErr = validateSlackTitle(newSlackChannel.title);

    setChannelError(channelErr);
    setTitleError(titleErr);

    if (channelErr || titleErr) return;

    const currentChannels = Array.isArray(changedValue)
      ? (changedValue as SlackCommunicationValue[])
      : [];

    const isDuplicate = currentChannels.some(
      ch => ch.channel === newSlackChannel.channel,
    );

    if (isDuplicate) {
      setChannelError('This channel is already added');
      return;
    }

    setChangedValue([...currentChannels, newSlackChannel]);
    setNewSlackChannel({ channel: '', title: '' });
    setChannelError(null);
    setTitleError(null);
  };

  const handleRemoveSlackChannel = (index: number) => {
    const currentChannels = Array.isArray(changedValue)
      ? (changedValue as SlackCommunicationValue[])
      : [];
    setChangedValue(currentChannels.filter((_, i) => i !== index));
  };

  const handleAddProject = () => {
    if (!newProjectKey) {
      setProjectError('Please select a project');
      return;
    }

    const currentProjects = normalizeToArray(changedValue);

    if (currentProjects.includes(newProjectKey)) {
      setProjectError('This project is already added');
      return;
    }

    setChangedValue([...currentProjects, newProjectKey]);
    setNewProjectKey('');
    setProjectError(null);
  };

  const handleRemoveProject = (projectKey: string) => {
    const currentProjects = normalizeToArray(changedValue);
    setChangedValue(currentProjects.filter(key => key !== projectKey));
  };

  const handleAddConfluenceUrl = () => {
    if (!newConfluenceUrl) {
      setConfluenceUrlError('Please enter a URL');
      return;
    }

    if (!isValidUrl(newConfluenceUrl)) {
      setConfluenceUrlError('Please enter a valid URL');
      return;
    }

    const currentUrls = normalizeToArray(changedValue);

    if (currentUrls.includes(newConfluenceUrl)) {
      setConfluenceUrlError('This URL is already added');
      return;
    }

    try {
      const parsedUrl = new URL(newConfluenceUrl);
      if (
        parsedUrl.hostname !== 'tomtom.atlassian.net' ||
        !parsedUrl.pathname.startsWith('/wiki/')
      ) {
        setConfluenceUrlError('Must be a valid Confluence URL');
        return;
      }
    } catch {
      setConfluenceUrlError('Please enter a valid URL');
      return;
    }

    setChangedValue([...currentUrls, newConfluenceUrl]);
    setNewConfluenceUrl('');
    setConfluenceUrlError(null);
  };

  const handleRemoveConfluenceUrl = (url: string) => {
    const currentUrls = normalizeToArray(changedValue);
    setChangedValue(currentUrls.filter(u => u !== url));
  };

  const getProjectDisplay = (projectKey: string) => {
    const project = propertyOptions?.find(opt => opt.id === projectKey);
    return project ? project.title : projectKey;
  };

  const hasChanges = () => {
    const initial = JSON.stringify(initialValue);
    const current = JSON.stringify(changedValue);
    return initial !== current;
  };

  const getChangesCount = () => {
    const initialArray = normalizeToArray(initialValue);
    const currentArray = normalizeToArray(changedValue);

    if (property.propertyType === 'COMMUNICATIONS_SLACK') {
      const initialChannels = Array.isArray(initialValue)
        ? initialValue.length
        : 0;
      const currentChannels = Array.isArray(changedValue)
        ? changedValue.length
        : 0;
      return Math.abs(currentChannels - initialChannels);
    }

    const added = currentArray.filter(
      item => !initialArray.includes(item),
    ).length;
    const removed = initialArray.filter(
      item => !currentArray.includes(item),
    ).length;
    return added + removed;
  };

  const handleSaveClick = useCallback(async () => {
    try {
      const current = await accentuateApi.get(stringifyEntityRef(entity));
      const diff = getDiffForType(property, changedValue, current);
      await accentuateApi.update(stringifyEntityRef(entity), diff);
      await accentuateApi.refresh(stringifyEntityRef(entity));

      alertApi.post({
        message:
          'Changes saved successfully. Please refresh the page to see updates.',
        severity: 'success',
        display: 'transient',
      });

      onClose?.();
    } catch {
      alertApi.post({
        message: 'Failed to update entity',
        severity: 'error',
        display: 'transient',
      });
    }
  }, [accentuateApi, alertApi, changedValue, entity, onClose, property]);

  const renderSlackInputs = () => {
    const channels = Array.isArray(changedValue)
      ? (changedValue as SlackCommunicationValue[])
      : [];

    return (
      <div className={classes.mainContainer}>
        <div className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            Add Slack Channel
          </Typography>

          <div className={classes.inputForm}>
            <TextField
              label="Slack channel"
              value={newSlackChannel.channel}
              onChange={e => {
                setNewSlackChannel({
                  ...newSlackChannel,
                  channel: e.target.value,
                });
                setChannelError(null);
              }}
              fullWidth
              variant="outlined"
              size="small"
              error={!!channelError}
              helperText={channelError || 'Example: #team-updates'}
              placeholder="#channel-name"
            />
            <TextField
              label="Slack URL"
              value={newSlackChannel.title}
              onChange={e => {
                setNewSlackChannel({
                  ...newSlackChannel,
                  title: e.target.value,
                });
                setTitleError(null);
              }}
              fullWidth
              variant="outlined"
              size="small"
              error={!!titleError}
              helperText={titleError || 'Full URL to the Slack channel'}
              placeholder="https://tomtomslack.slack.com/archives/CHANNEL_ID"
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddSlackChannel}
              disabled={
                !newSlackChannel.channel ||
                !newSlackChannel.title ||
                !!channelError ||
                !!titleError
              }
              className={classes.addButton}
              fullWidth
            >
              Add to List
            </Button>
          </div>
        </div>

        <div className={classes.itemsSection}>
          <Typography className={classes.itemsSectionTitle}>
            Slack Channels in this Group({channels.length})
          </Typography>

          {channels.length > 0 ? (
            <div className={classes.itemsList}>
              {channels.map((channel, index) => (
                <div key={index} className={classes.itemCard}>
                  <div className={classes.itemInfo}>
                    <span className={classes.itemName}>{channel.channel}</span>
                    <a
                      href={channel.title}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.itemLink}
                    >
                      {channel.title}
                    </a>
                  </div>
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<CloseIcon />}
                    onClick={() => handleRemoveSlackChannel(index)}
                    className={classes.removeButton}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.emptyState}>
              No Slack channels yet. Add one above to get started.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderJiraInputs = () => {
    const projects = normalizeToArray(changedValue);

    return (
      <div className={classes.mainContainer}>
        <div className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            Add Jira Project
          </Typography>

          <div className={classes.inputForm}>
            <MultivalueInput
              options={propertyOptions || []}
              initialSelection={newProjectKey}
              multiple={false}
              onChange={ids => {
                setNewProjectKey(ids[0] || '');
                setProjectError(null);
              }}
            />

            {projectError && (
              <Typography className={classes.errorText}>
                {projectError}
              </Typography>
            )}

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddProject}
              disabled={!newProjectKey || !!projectError}
              className={classes.addButton}
              fullWidth
            >
              Add to List
            </Button>
          </div>
        </div>

        <div className={classes.itemsSection}>
          <Typography className={classes.itemsSectionTitle}>
            Jira Projects in This Group ({projects.length})
          </Typography>

          {projects.length > 0 ? (
            <div className={classes.itemsList}>
              {projects.map((projectKey, index) => (
                <div key={index} className={classes.itemCard}>
                  <div className={classes.itemInfo}>
                    <span className={classes.itemName}>
                      {getProjectDisplay(projectKey)}
                    </span>
                  </div>
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<CloseIcon />}
                    onClick={() => handleRemoveProject(projectKey)}
                    className={classes.removeButton}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.emptyState}>
              No Jira projects yet. Add one above to get started.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderConfluenceInputs = () => {
    const urls = normalizeToArray(changedValue);

    return (
      <div className={classes.mainContainer}>
        <div className={classes.formSection}>
          <Typography className={classes.sectionTitle}>
            Add Confluence URL
          </Typography>

          <div className={classes.inputForm}>
            <TextField
              label="Confluence URL"
              value={newConfluenceUrl}
              onChange={e => {
                setNewConfluenceUrl(e.target.value);
                setConfluenceUrlError(null);
              }}
              fullWidth
              variant="outlined"
              size="small"
              error={!!confluenceUrlError}
              helperText={
                confluenceUrlError || 'Must be a valid Confluence wiki URL'
              }
              placeholder="https://tomtom.atlassian.net/wiki/..."
            />

            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddConfluenceUrl}
              disabled={!newConfluenceUrl || !!confluenceUrlError}
              className={classes.addButton}
              fullWidth
            >
              Add to List
            </Button>
          </div>
        </div>

        <div className={classes.itemsSection}>
          <Typography className={classes.itemsSectionTitle}>
            Confluence URLs in This Group ({urls.length})
          </Typography>

          {urls.length > 0 ? (
            <div className={classes.itemsList}>
              {urls.map((url, index) => (
                <div key={index} className={classes.itemCard}>
                  <div className={classes.itemInfo}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={classes.itemLink}
                    >
                      {url}
                    </a>
                  </div>
                  <Button
                    size="small"
                    color="secondary"
                    startIcon={<CloseIcon />}
                    onClick={() => handleRemoveConfluenceUrl(url)}
                    className={classes.removeButton}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className={classes.emptyState}>
              No Confluence URLs yet. Add one above to get started.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderDefaultInput = () => {
    if (propertyOptions?.length) {
      let initialSelection: string;
      if (Array.isArray(changedValue)) {
        initialSelection = changedValue.join(',');
      } else {
        initialSelection = (changedValue as string) || '';
      }

      return (
        <MultivalueInput
          options={propertyOptions}
          initialSelection={initialSelection}
          multiple={property.propertyType !== 'OWNER'}
          onChange={ids => {
            if (
              property.propertyType === 'ANNOTATION' ||
              property.propertyType === 'OWNER'
            ) {
              setChangedValue(ids.join(','));
            } else {
              setChangedValue(ids);
            }
          }}
        />
      );
    }

    return (
      <TextField
        label="New value"
        value={changedValue as string}
        onChange={e => setChangedValue(e.target.value)}
        fullWidth
        variant="outlined"
      />
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (property.propertyType === 'COMMUNICATIONS_SLACK') {
      return renderSlackInputs();
    }

    if (
      property.propertyType === 'ANNOTATION' &&
      property.propertyValue === 'jira/project-key'
    ) {
      return renderJiraInputs();
    }

    if (
      property.propertyType === 'ANNOTATION' &&
      property.propertyValue === 'confluence/wiki-urls'
    ) {
      return renderConfluenceInputs();
    }

    return renderDefaultInput();
  };

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>
        <div className={classes.header}>
          <RepositoryIcon color="primary" />
          <Typography variant="h6">{dialogTitle}</Typography>
        </div>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {renderContent()}

        <div className={classes.actionButtons}>
          <div className={classes.changesSummary}>
            {hasChanges() && (
              <Chip
                label={`${getChangesCount()} ${
                  getChangesCount() === 1 ? 'change' : 'changes'
                } pending`}
                color="primary"
                size="small"
                className={classes.changesChip}
              />
            )}
          </div>

          <div className={classes.buttonGroup}>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveClick}
              disabled={loading || !hasChanges()}
              className={classes.saveButton}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
